import type { ReactNode } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { AmazonExperimentMetrics, AmazonMetricValue } from "@/types/ab-tests";

interface AmazonMetricsTableProps {
  metrics: AmazonExperimentMetrics;
}

/**
 * WEB-002: an unresolved cell (missing side, or a fully-unresolved metric) renders a muted dash
 * with a short reason instead of dropping the row — the table always keeps its full shape.
 */
const NOT_FOUND_REASON = "Not found in this upload";

function EmptyCell() {
  return (
    <span className="text-muted-foreground cursor-help" title={NOT_FOUND_REASON}>
      —
    </span>
  );
}

function formatSide(value: number | string | null | undefined, numeric: boolean): ReactNode {
  if (value === null || value === undefined) return <EmptyCell />;
  return numeric ? (value as number).toLocaleString() : value;
}

interface MetricRow {
  label: string;
  metric: AmazonMetricValue<number | string> | null | undefined;
  numeric: boolean;
}

export function AmazonMetricsTable({ metrics }: AmazonMetricsTableProps) {
  const rows: MetricRow[] = [
    { label: "Conversion", metric: metrics.conversion, numeric: false },
    { label: "Units Per Unique Visitor", metric: metrics.unitsPerUniqueVisitor, numeric: true },
    { label: "Units Sold", metric: metrics.unitsSold, numeric: true },
    { label: "Units Sold From Search", metric: metrics.unitsSoldFromSearch, numeric: true },
    { label: "Sales", metric: metrics.sales, numeric: false },
    { label: "Sales From Search", metric: metrics.salesFromSearch, numeric: false },
    { label: "Sample Size", metric: metrics.sampleSize, numeric: true },
  ];

  return (
    <div className="border overflow-hidden rounded-md">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Metric</TableHead>
            <TableHead className="text-right">Variant A</TableHead>
            <TableHead className="text-right">Variant B</TableHead>
            <TableHead className="text-right">Difference</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="font-medium">{row.label}</TableCell>
              <TableCell className="text-right">{formatSide(row.metric?.a, row.numeric)}</TableCell>
              <TableCell className="text-right">{formatSide(row.metric?.b, row.numeric)}</TableCell>
              <TableCell className="text-right">{formatSide(row.metric?.diff, row.numeric)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
